import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { useAuth } from '../context/AuthContext';
import { format, differenceInDays, startOfDay } from 'date-fns';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiFileText, FiGitPullRequest, FiTrendingUp, FiCalendar, FiActivity, FiAlertTriangle } = FiIcons;

const Dashboard = () => {
  const { clients, contracts, changeRequests, works, settings, loading } = useCRM();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Redirect non-admins to Tasks page immediately
  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/tasks');
    }
  }, [loading, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If not admin, return null while redirect happens
  if (!isAdmin) return null;

  // Calculate stats
  const activeContracts = contracts.filter(c => c.status === 'active').length;
  const pendingRequests = changeRequests.filter(r => r.status === 'pending').length;
  const activeWorks = works?.filter(w => w.status === 'in-progress').length || 0;

  // Calculate renewal alerts with specific filtering rules
  const renewalAlerts = contracts.filter(contract => {
    // Only check active contracts
    if (contract.status !== 'active') return false;
    
    // Normalize dates to start of day to avoid time discrepancies
    const endDate = startOfDay(new Date(contract.endDate));
    const today = startOfDay(new Date());
    const daysUntilExpiry = differenceInDays(endDate, today);
    
    // Rule 1: Expired - Show only if expired within the last 20 days
    // daysUntilExpiry is negative for expired dates (e.g., -1 is yesterday)
    const isRecentlyExpired = daysUntilExpiry < 0 && daysUntilExpiry >= -20;

    // Rule 2: Upcoming - Show start showing before 15 days
    const isUpcoming = daysUntilExpiry >= 0 && daysUntilExpiry <= 15;

    return isRecentlyExpired || isUpcoming;
  }).sort((a, b) => new Date(a.endDate) - new Date(b.endDate)); // Sort by date ascending (oldest/soonest first)

  // Recent activity
  const recentActivity = [
    ...contracts.slice(-5).map(c => ({
      id: c.id,
      type: 'contract',
      title: `New contract: ${c.title}`,
      date: c.createdAt,
    })),
    ...changeRequests.slice(-5).map(r => ({
      id: r.id,
      type: 'change-request',
      title: `Change request: ${r.title}`,
      date: r.createdAt,
    })),
    ...(works?.slice(-5).map(w => ({
      id: w.id,
      type: 'work',
      title: `Work: ${w.title}`,
      date: w.createdAt,
    })) || []),
  ]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  const stats = [
    {
      name: 'Total Clients',
      value: clients.length,
      icon: FiUsers,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      name: 'Active Contracts',
      value: activeContracts,
      icon: FiFileText,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      name: 'Active Works',
      value: activeWorks,
      icon: FiActivity,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      name: 'Pending Requests',
      value: pendingRequests,
      icon: FiGitPullRequest,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <div className="text-sm text-gray-500">
          {format(new Date(), 'EEEE, MMMM do, yyyy')}
        </div>
      </div>

      {/* Connection Status */}
      <Card className="p-4 bg-green-50 border-green-200">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-sm font-medium text-green-800">
            Connected to Supabase Database
          </span>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bg}`}>
                  <SafeIcon icon={stat.icon} className={`h-6 w-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Renewal Alerts */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              Contract Alerts
              {renewalAlerts.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                  {renewalAlerts.length}
                </span>
              )}
            </h2>
            <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-2 custom-scrollbar">
            {renewalAlerts.length === 0 ? (
              <p className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                No immediate renewals (next 15 days) or recent expirations (last 20 days).
              </p>
            ) : (
              renewalAlerts.map(contract => {
                const client = clients.find(c => c.id === contract.clientId);
                const endDate = startOfDay(new Date(contract.endDate));
                const today = startOfDay(new Date());
                const daysLeft = differenceInDays(endDate, today);
                const isExpired = daysLeft < 0;
                
                return (
                  <div 
                    key={contract.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${
                      isExpired 
                        ? 'bg-red-50 border-red-500' 
                        : daysLeft <= 7 
                          ? 'bg-orange-50 border-orange-500' 
                          : 'bg-yellow-50 border-yellow-400'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{contract.title}</p>
                      <p className="text-sm text-gray-600 truncate max-w-[200px]">
                        {client?.name || 'Unknown Client'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${isExpired ? 'text-red-700' : 'text-yellow-700'}`}>
                        {isExpired 
                          ? `Expired ${Math.abs(daysLeft)} days ago` 
                          : daysLeft === 0 
                            ? 'Expires Today' 
                            : `${daysLeft} days left`
                        }
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(contract.endDate), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            ) : (
              recentActivity.map(activity => (
                <div key={activity.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className={`p-2 rounded-full flex-shrink-0 ${
                    activity.type === 'contract' ? 'bg-green-100' : 
                    activity.type === 'work' ? 'bg-purple-100' : 'bg-blue-100'
                  }`}>
                    <SafeIcon 
                      icon={
                        activity.type === 'contract' ? FiFileText : 
                        activity.type === 'work' ? FiActivity : FiGitPullRequest
                      } 
                      className={`h-4 w-4 ${
                        activity.type === 'contract' ? 'text-green-600' : 
                        activity.type === 'work' ? 'text-purple-600' : 'text-blue-600'
                      }`} 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(activity.date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;